// Teacher Installs (Phase 3 Module 16) — admin-side data layer.
//
// Thin wrappers over rpc_list_teachers_install_status. All cross-user
// visibility is enforced in-DB (SECURITY DEFINER + admin role guard);
// we just keep call sites tidy and normalise the shape.

import { supabase, isSupabaseConfigured } from "./supabase";

/**
 * Returns per-teacher install status for the admin's org, already ordered
 * "not installed first, then by name". Resilient: pre-migration or for
 * non-admin callers the RPC returns zero rows (or errors to empty) so
 * the dashboard card silently hides.
 */
export async function listTeachersInstallStatus() {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase.rpc("rpc_list_teachers_install_status");
  if (error) {
    // Resilient defensive default — non-admin callers, missing migration,
    // or transient network races (e.g. tab backgrounding) all land here.
    // We swallow silently so the dashboard card just hides instead of
    // polluting the production console. Inspect via DevTools "Verbose"
    // level if you need to debug.
    // eslint-disable-next-line no-console
    console.debug("[teacherInstalls] RPC fallback to []:", error.message);
    return [];
  }
  return data || [];
}

/** How many teachers haven't enrolled a push device yet. */
export function countMissingPush(rows) {
  if (!Array.isArray(rows)) return 0;
  return rows.filter((r) => !r.has_push).length;
}

/**
 * Display helper: "2 days ago" / "Never" for last-seen pill.
 * Kept intentionally dumb so the UI layer doesn't need date-fns.
 */
export function lastSeenLabel(iso) {
  if (!iso) return "Never";
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days < 1)   return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7)   return `${days} days ago`;
  if (days < 30)  return `${Math.floor(days / 7)} weeks ago`;
  const months = Math.floor(days / 30);
  return months >= 12 ? `${Math.floor(months / 12)}y ago` : `${months}mo ago`;
}
